/**
 * 分頁圖示。
 *
 * 先前這裡是五個 Unicode 符號（⌂ ▦ ▥ ★ ⚙）。三個問題：
 *
 * 1. **▦ 與 ▥ 是「網底方塊」不是月曆與長條圖**（U+25A6／U+25A5），它們不表意。
 * 2. **⚙ U+2699 在 iOS 有 emoji 呈現**，實機上那一顆是彩色的，旁邊四顆是黑的。
 * 3. 這些字不在子集字型裡，會掉回系統字體，跟旁邊的中文標籤不同調——而且
 *    「長什麼樣」取決於使用者的裝置，我們控制不了。
 *
 * 改成手繪 SVG：同一個 24 方格、同一個 2.5px 描邊、圓端圓角，跟卡片的
 * 3px 黑框是同一種筆觸。字型、平台、emoji 設定都影響不到它們。
 *
 * `分享` 順手換掉語意：星星在遊戲介面通常代表收藏或評價，這裡是分享。
 */
export type IconName =
  | "today" | "calendar" | "stats" | "share" | "settings" | "prediction" | "cheer";

const PATHS: Record<IconName, preact.JSX.Element> = {
  today: <><path d="M4 11.2 12 4.5l8 6.7" /><path d="M6.4 10.6V19h11.2v-8.4" /><path d="M10.2 19v-4.4h3.6V19" /></>,
  calendar: <>
    <rect x="4" y="5.5" width="16" height="14.5" rx="2.5" />
    <path d="M4 10.2h16" /><path d="M8.6 3.6v3.6M15.4 3.6v3.6" /><path d="M8.4 14.4h1.8M14 14.4h1.8" />
  </>,
  stats: <><path d="M4 20h16" /><path d="M7.2 20v-6.4M12 20V7.6M16.8 20v-9.6" /></>,
  share: <>
    <circle cx="17.6" cy="6.4" r="2.9" /><circle cx="6.4" cy="12" r="2.9" /><circle cx="17.6" cy="17.6" r="2.9" />
    <path d="M9 10.7l6-3M9 13.3l6 3" />
  </>,
  /*
   * 設定畫滑桿不畫齒輪：齒輪的齒在 22px、2.5px 描邊下糊成一圈放射線，
   * 讀起來是太陽不是齒輪——第一版就是這樣，截圖放大才看出來。
   */
  settings: <>
    <path d="M4 8h4.6M13.4 8H20" /><circle cx="11" cy="8" r="2.6" />
    <path d="M4 16h7.4M16.2 16H20" /><circle cx="13.8" cy="16" r="2.6" />
  </>,
  // 競猜：擲出去的骰子——押注，不是抽獎
  prediction: <>
    <rect x="4.2" y="4.2" width="15.6" height="15.6" rx="3.4" />
    <path d="M8.8 8.8h.1M15.2 8.8h.1M12 12h.1M8.8 15.2h.1M15.2 15.2h.1" stroke-width="3.2" />
  </>,
  cheer: <path d="M12 19.6C6.9 16.3 4 13.4 4 10.2A4.2 4.2 0 0 1 12 8a4.2 4.2 0 0 1 8 2.2c0 3.2-2.9 6.1-8 9.4Z" />
};

export function TabIcon({ name }: { name: IconName }) {
  return (
    <svg
      class="tab-icon" viewBox="0 0 24 24" aria-hidden="true"
      fill="none" stroke="currentColor" stroke-width="2.5"
      stroke-linecap="round" stroke-linejoin="round"
    >
      {PATHS[name]}
    </svg>
  );
}
